<mat-card
  class="rounded-3xl border border-gray-6/60 shadow-sm overflow-hidden bg-white/70 dark:bg-gray-1/70 backdrop-blur-xl"
>
  <!-- Card Header -->
  <div
    class="bg-gray-2/60 py-4 px-6 border-b border-gray-6/60 flex flex-col sm:flex-row sm:items-center justify-between gap-4 w-full"
  >
    <div class="flex items-center gap-3">
      <div
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors"
        [class.bg-amber-a3]="todoCount() > 0"
        [class.text-amber-11]="todoCount() > 0"
        [class.bg-ruby-a3]="todoCount() === 0 && flaggedCount() > 0"
        [class.text-ruby-11]="todoCount() === 0 && flaggedCount() > 0"
        [class.bg-jade-a3]="todoCount() === 0 && flaggedCount() === 0"
        [class.text-jade-11]="todoCount() === 0 && flaggedCount() === 0"
      >
        <mat-icon class="w-5! h-5! text-[20px]! flex items-center justify-center leading-none">
          {{ todoCount() > 0 ? 'pending_actions' : flaggedCount() > 0 ? 'policy' : 'verified_user' }}
        </mat-icon>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-12 m-0 flex items-center gap-2">
          Modération & Détection de contournement
        </h3>
        <p class="text-xs text-gray-11 m-0">Gestion individuelle des signalements (Loi Hoguet & CGU)</p>
      </div>
    </div>

    <!-- Summary Stats Badges in Header -->
    <div class="flex flex-wrap items-center gap-2">
      @if (todoCount() > 0) {
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-a3 text-amber-11 border border-amber-a6 font-bold text-xs shadow-2xs tracking-wide"
        >
          <span class="w-2 h-2 rounded-full bg-amber-9 animate-pulse"></span>
          {{ todoCount() }} à traiter
        </span>
      }
      @if (flaggedCount() > 0) {
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ruby-a3 text-ruby-11 border border-ruby-a6 font-bold text-xs shadow-2xs tracking-wide"
        >
          <mat-icon class="text-ruby-9 text-sm w-3.5 h-3.5">flag</mat-icon>
          {{ flaggedCount() }} confirmé{{ flaggedCount() > 1 ? 's' : '' }}
        </span>
      }
      @if (falsePositiveCount() > 0) {
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-bold text-xs shadow-2xs tracking-wide"
        >
          <mat-icon class="text-jade-9 text-sm w-3.5 h-3.5">check</mat-icon>
          {{ falsePositiveCount() }} faux positif{{ falsePositiveCount() > 1 ? 's' : '' }}
        </span>
      }
      @if (history().length === 0) {
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-bold text-xs shadow-2xs tracking-wide"
        >
          <mat-icon class="text-jade-9 text-sm w-3.5 h-3.5">check_circle</mat-icon>
          Conforme
        </span>
      }
    </div>
  </div>

  <mat-card-content class="p-6! space-y-6">
    @if (todoCount() > 0) {
      <!-- Banner when there are items to review -->
      <div class="rounded-2xl p-4 bg-amber-1 border border-amber-a6 text-amber-12 flex items-start gap-3.5">
        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-3 text-amber-11">
          <mat-icon class="text-base w-4 h-4">warning</mat-icon>
        </div>
        <div class="text-xs leading-relaxed flex-1">
          <span class="font-bold text-amber-12">
            {{ todoCount() }} signalement{{ todoCount() > 1 ? 's' : '' }} en attente de traitement :
          </span>
          <p class="mt-1 text-amber-11">
            Veuillez examiner chaque détection ci-dessous et la qualifier en tant que
            <strong>« Faux positif »</strong> ou <strong>« Signalement confirmé »</strong>.
          </p>
        </div>
      </div>
    }

    @if (history().length > 0) {
      <!-- Individual Entries List -->
      <div class="space-y-4">
        <div class="text-xs font-bold text-gray-11 uppercase tracking-wider px-1">
          Liste des signalements détectés ({{ history().length }})
        </div>

        <div class="grid grid-cols-1 gap-4">
          @for (entry of history(); track entry._id || $index) {
            <div
              class="rounded-2xl p-5 border flex flex-col gap-4 transition-all"
              [class.border-amber-6]="!entry.status || entry.status === ModerationStatus.TODO"
              [class.bg-amber-1/30]="!entry.status || entry.status === ModerationStatus.TODO"
              [class.border-ruby-6]="entry.status === ModerationStatus.FLAGGED"
              [class.bg-ruby-1/20]="entry.status === ModerationStatus.FLAGGED"
              [class.border-jade-6]="entry.status === ModerationStatus.FALSE_POSITIVE"
              [class.bg-jade-1/20]="entry.status === ModerationStatus.FALSE_POSITIVE"
            >
              <!-- Top Row: Field & Entry Status -->
              <div class="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-gray-5/60">
                <div class="flex items-center gap-2">
                  <span
                    class="px-2.5 py-0.5 rounded-md text-xs font-bold bg-indigo-a3 text-indigo-11 border border-indigo-a4"
                  >
                    {{ getFieldLabel(entry.field) }}
                  </span>
                  <span class="text-[11px] font-mono text-gray-10">({{ entry.field }})</span>
                </div>

                <!-- Individual Status Badge -->
                <div class="flex items-center gap-2">
                  @switch (entry.status) {
                    @case (ModerationStatus.FALSE_POSITIVE) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-jade-a3 text-jade-11 border border-jade-a5"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5!">check_circle</mat-icon>
                        Faux positif
                      </span>
                    }
                    @case (ModerationStatus.FLAGGED) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-ruby-a3 text-ruby-11 border border-ruby-a5"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5!">flag</mat-icon>
                        Signalement confirmé
                      </span>
                    }
                    @default {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-a3 text-amber-11 border border-amber-a5"
                      >
                        <mat-icon class="text-xs! w-3.5! h-3.5!">hourglass_top</mat-icon>
                        À traiter
                      </span>
                    }
                  }

                  @if (entry.flaggedAt) {
                    <span class="text-xs text-gray-10 flex items-center gap-1 font-medium ml-2">
                      <mat-icon class="text-xs! w-3.5! h-3.5!">schedule</mat-icon>
                      {{ entry.flaggedAt | date: 'dd/MM/yyyy à HH:mm' }}
                    </span>
                  }
                </div>
              </div>

              <!-- Detected reasons -->
              @if (entry.reasons?.length) {
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="text-[11px] text-gray-11 font-medium mr-1">Motifs détectés :</span>
                  @for (reason of entry.reasons; track reason) {
                    <span
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-ruby-a3 text-ruby-11 border border-ruby-a5"
                    >
                      <mat-icon class="text-[12px]! w-3! h-3!">warning</mat-icon>
                      {{ getReasonLabel(reason) }}
                    </span>
                  }
                </div>
              }

              <!-- Raw value box -->
              <div class="rounded-xl p-3 bg-gray-2 dark:bg-gray-3 border border-gray-5">
                <div class="text-[10px] uppercase font-bold text-gray-10 mb-1 flex items-center gap-1">
                  <mat-icon class="text-[12px]! w-3! h-3!">search</mat-icon>
                  Contenu brut capturé à la soumission :
                </div>
                <div class="text-xs font-mono text-gray-12 break-all select-all font-medium">
                  {{ entry.rawValue }}
                </div>
              </div>

              <!-- Review audit trail (if already reviewed) -->
              @if (entry.reviewedAt) {
                <div class="text-xs text-gray-11 flex items-center gap-1.5 pt-1">
                  <mat-icon class="text-sm! w-4! h-4! text-gray-9">verified</mat-icon>
                  <span>
                    Traité le
                    <strong>{{ entry.reviewedAt | date: 'dd/MM/yyyy à HH:mm' }}</strong>
                    @if (getReviewerName(entry); as reviewer) {
                      par <strong>{{ reviewer }}</strong>
                    }
                  </span>
                  @if (entry.reviewNote) {
                    <span class="italic text-gray-10 ml-2">« {{ entry.reviewNote }} »</span>
                  }
                </div>
              }

              <!-- Action buttons for this specific entry -->
              <div class="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-gray-5/40">
                @let isLoading = loadingEntryId() === entry._id;

                @if (isLoading) {
                  <mat-spinner diameter="20" class="mr-2" />
                } @else {
                  @if (!entry.status || entry.status === ModerationStatus.TODO) {
                    <!-- Option 1: Faux positif -->
                    <button
                      mat-stroked-button
                      type="button"
                      class="text-xs font-semibold"
                      (click)="updateEntryStatus(entry, ModerationStatus.FALSE_POSITIVE)"
                    >
                      <mat-icon class="text-sm! w-4! h-4! text-jade-10">check</mat-icon>
                      <span>Faux positif</span>
                    </button>

                    <!-- Option 2: Flagged / Confirmé -->
                    <button
                      mat-stroked-button
                      type="button"
                      class="primary destructive text-xs font-semibold"
                      (click)="updateEntryStatus(entry, ModerationStatus.FLAGGED)"
                    >
                      <mat-icon class="text-sm! w-4! h-4!">flag</mat-icon>
                      <span>Confirmer signalement</span>
                    </button>
                  } @else {
                    <!-- Option 3: Reset to Todo (if currently false positive or flagged) -->
                    <button
                      mat-button
                      type="button"
                      class="text-xs text-gray-11 hover:text-gray-12"
                      (click)="updateEntryStatus(entry, ModerationStatus.TODO)"
                    >
                      <mat-icon class="text-sm! w-4! h-4!">undo</mat-icon>
                      <span>Remettre à traiter</span>
                    </button>
                  }
                }
              </div>
            </div>
          }
        </div>
      </div>
    } @else {
      <!-- Clean state -->
      <div
        class="rounded-2xl p-6 bg-jade-1 border border-jade-a6 text-jade-12 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-jade-3 text-jade-11 shadow-xs"
        >
          <mat-icon class="text-2xl w-6 h-6">verified_user</mat-icon>
        </div>
        <div>
          <h4 class="text-sm font-bold text-jade-12 m-0">Aucun signalement de modération</h4>
          <p class="text-xs text-jade-11 m-0 mt-1 leading-relaxed">
            Les informations renseignées sur ce bien sont saines et conformes aux règles de la plateforme.
            Aucun numéro de téléphone, email direct ou lien non autorisé n'a été détecté.
          </p>
        </div>
      </div>
    }
  </mat-card-content>
</mat-card>
